import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiChevronDown, FiLogOut, FiPhoneCall } from 'react-icons/fi';
import { AiOutlineDashboard } from 'react-icons/ai';

import logo from '../assets/logo.webp';
import avatar from '../assets/avatar.webp';
import { dialerCrmRequest, getDialerCrmSession, requestError, SESSION_KEY } from '../utils/dialerCrmApi';
import '../pages/Dashboard.scss';

export default function DashboardLayout({ children }) {
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [loggingOut, setLoggingOut] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;
        const session = getDialerCrmSession();
        if (!session) {
            sessionStorage.removeItem(SESSION_KEY);
            navigate('/', { replace: true });
            return undefined;
        }
        dialerCrmRequest('profile', {}, session.token).then(response => {
            if (!active) return;
            if (response.status !== 1) {
                sessionStorage.removeItem(SESSION_KEY);
                navigate('/', { replace: true });
            } else setUser(response.data);
        }).catch(failure => {
            if (!active) return;
            if (failure.response?.status === 401) {
                sessionStorage.removeItem(SESSION_KEY);
                navigate('/', { replace: true });
            } else setError(requestError(failure));
        });
        const timer = setTimeout(() => {
            sessionStorage.removeItem(SESSION_KEY);
            navigate('/', { replace: true });
        }, Math.max(0, session.expires_at * 1000 - Date.now()));
        return () => { active = false; clearTimeout(timer); };
    }, [navigate]);

    const logout = async () => {
        setLoggingOut(true);
        try {
            const session = getDialerCrmSession();
            if (session) await dialerCrmRequest('logout', {}, session.token);
            sessionStorage.removeItem(SESSION_KEY);
            navigate('/', { replace: true });
        } catch (failure) {
            toast.error(requestError(failure));
            setLoggingOut(false);
        }
    };

    const name = user?.subadmin_name || 'Subadmin User';
    const profileImage = user?.subadmin_foto && user.subadmin_foto !== 'user.png'
        ? `https://satkartar.one/assets/subadmin/${encodeURIComponent(user.subadmin_foto)}` : avatar;

    return <div className="dialer-crm-dashboard">
        <Helmet><title>Satkartar | Dialer with CRM</title></Helmet>
        <header className="crm-topbar">
            <Link to="/dashboard" className="crm-brand" aria-label="Sat Kartar dashboard">
                <img src={logo} alt="SAT KARTAR" />
            </Link>
            <details className="crm-profile">
                <summary aria-label="Open profile menu">
                    <img className="crm-avatar" src={profileImage} alt="" onError={event => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = avatar;
                    }} />
                    <span className="crm-profile-copy"><strong>{user ? name : 'Loading...'}</strong><span>Lead Push Panel</span></span>
                    <FiChevronDown aria-hidden="true" />
                </summary>
                <div className="crm-profile-menu">
                    <button type="button" onClick={logout} disabled={loggingOut}>
                        <FiLogOut /> {loggingOut ? 'Logging out...' : 'Logout'}
                    </button>
                </div>
            </details>
        </header>
        <aside className="crm-sidebar" aria-label="Main navigation">
            <nav>
                <NavLink to="/dashboard" className={({ isActive }) => `crm-nav-item${isActive ? ' crm-nav-current' : ''}`}><AiOutlineDashboard /><span>Dashboard</span></NavLink>
                <NavLink to="/dialer-campaign" className={({ isActive }) => `crm-nav-item${isActive ? ' crm-nav-current' : ''}`}><FiPhoneCall /><span>Lead Push</span></NavLink>
            </nav>
        </aside>
        <main className="crm-main">
            {error ? <div className="crm-state" role="alert">{error} <button type="button" onClick={() => window.location.reload()}>Retry</button></div> :
                user ? children(user) : null}
        </main>
        <footer className="crm-footer">
            <span>&copy; 2026 Sat Kartar Life Limited.</span>{' '}
            <span>All Rights Reserved</span>
        </footer>
    </div>;
}
